/**
 * TT-02 / TT-03 — TanStack Query hooks for `time-tracking/v1@1.0.0`.
 *
 *   GET    /trackings                       → useTimeTrackingsQuery
 *   POST   /trackings                       → useCreateTimeTrackingMutation
 *   PATCH  /trackings/{id}                  → useUpdateTimeTrackingMutation
 *   DELETE /trackings/{id}                  → useDeleteTimeTrackingMutation
 *   POST   /trackings/lock                  → useLockTimeTrackingsMutation
 *   GET    /my-context                      → useMyTimeTrackingContextQuery
 *   GET    /projects/{id}/config            → useTimeTrackingConfigQuery
 *   PUT    /projects/{id}/config            → usePutTimeTrackingConfigMutation
 *
 * Every query is `enabled`-gated on tenant readiness: a tenant-scoped call fired
 * before the slug resolves goes out with no `X-Tenant-ID` and 400s. That gate is
 * a repo-wide rule for tenant-scoped queries, not a local nicety.
 */
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@aeriosiq/auth-client";

import { timeTrackingFetch } from "./timeTrackingFetch.js";
import { timeTrackingKeys } from "./keys.js";
import type {
  MyTimeTrackingContextResponse,
  TimeTracking,
  TimeTrackingConfig,
  TimeTrackingConfigPutRequest,
  TimeTrackingCreateRequest,
  TimeTrackingListFilters,
  TimeTrackingListResponse,
  TimeTrackingLockResponse,
  TimeTrackingPatchRequest,
  TimeTrackingTotals,
} from "./types.js";

/** Serialise the filter set into a `time-tracking/v1` list query string. */
export function buildListQuery(filters: TimeTrackingListFilters): string {
  const p = new URLSearchParams();
  if (filters.from) p.set("from", filters.from);
  if (filters.to) p.set("to", filters.to);
  if (filters.activityType) p.set("activityType", filters.activityType);
  if (filters.locked !== undefined) p.set("locked", String(filters.locked));
  if (filters.userId) p.set("userId", filters.userId);
  if (filters.projectId) p.set("projectId", filters.projectId);
  if (filters.entityType) p.set("entityType", filters.entityType);
  if (filters.entityId) p.set("entityId", filters.entityId);
  if (filters.page != null) p.set("page", String(filters.page));
  if (filters.pageSize != null) p.set("pageSize", String(filters.pageSize));
  // Only emit `scope` when it is the non-default — an absent param means `own`,
  // so sending it explicitly would only churn the query key.
  if (filters.scope === "member") p.set("scope", "member");
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

/** True once the tenant slug is known — see the module header. */
function useTenantReady(): boolean {
  const tenant = useAuthStore((s) => s.tenant);
  return Boolean(tenant?.tenantSlug);
}

export function useTimeTrackingsQuery(
  filters: TimeTrackingListFilters,
  options: { enabled?: boolean } = {},
) {
  const tenantReady = useTenantReady();
  return useQuery<TimeTrackingListResponse>({
    queryKey: timeTrackingKeys.list(filters),
    queryFn: () =>
      timeTrackingFetch<TimeTrackingListResponse>(
        `/trackings${buildListQuery(filters)}`,
      ),
    enabled: tenantReady && (options.enabled ?? true),
    staleTime: 15_000,
    retry: false,
  });
}

/**
 * The pilot's assignments, pre-resolved into anchor chains. Cached longer than
 * the list: an assignment set changes on the scale of a working day, and the
 * create form should open with chips already populated rather than empty.
 */
export function useMyTimeTrackingContextQuery(
  options: { enabled?: boolean } = {},
) {
  const tenantReady = useTenantReady();
  return useQuery<MyTimeTrackingContextResponse>({
    queryKey: timeTrackingKeys.myContext(),
    queryFn: () =>
      timeTrackingFetch<MyTimeTrackingContextResponse>("/my-context"),
    enabled: tenantReady && (options.enabled ?? true),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * TT-05 — the PILOT's list: their own trackings, wherever those live.
 *
 * A pilot employed by a subcontractor books standby on the project owner's
 * anchor, and that row is stored in the OWNER's schema (XT-D29). So "my
 * trackings" is genuinely two reads:
 *
 *   scope=own      rows in our own tenant's schema — the ordinary case,
 *                  paginated.
 *   scope=member   rows we may see in granted owners' schemas — un-paginated,
 *                  bounded by the grants.
 *
 * The server deliberately does not merge them (different schemas, different
 * capability rules), so the merge happens here: concatenate, then re-sort by
 * `startTime` descending so the combined list reads as one timeline.
 *
 * `member` is fetched unconditionally. A tenant with no grants gets an empty
 * list from a cheap query, and gating it on "am I a subcontractor?" would need
 * a fact the client does not reliably have — the grant lives server-side.
 */
export function useMyTimeTrackingsQuery(
  filters: TimeTrackingListFilters,
  options: { enabled?: boolean } = {},
) {
  const own = useTimeTrackingsQuery(filters, options);
  const member = useTimeTrackingsQuery(
    { ...filters, scope: "member" },
    options,
  );

  const data = useMemo(
    () => mergeTrackingPages(own.data, member.data),
    [own.data, member.data],
  );

  return {
    data,
    // Pending until BOTH have answered — showing the own half first would make
    // a subcontractor's list visibly "fill in", which reads as a bug.
    isPending: own.isPending || member.isPending,
    isError: own.isError || member.isError,
    // The own half is the one whose failure means the page is broken; a member
    // failure with a working own half still shows a usable list.
    error: own.error ?? member.error,
    refetch: async () => {
      await Promise.all([own.refetch(), member.refetch()]);
    },
  };
}

/**
 * Merge the own-schema page with the member-schema set into one timeline.
 *
 * Pure and exported so the merge rules are testable without a React tree —
 * they are the part that can actually be wrong.
 *
 * `total` is the SUM OF THE REPORTED TOTALS, not `items.length`: the own side
 * is paginated, so its `items` is one page while its `total` is the full count.
 * Using the array length would make the footer under-report the moment a pilot
 * has more than one page of standby.
 */
export function mergeTrackingPages(
  own: TimeTrackingListResponse | undefined,
  member: TimeTrackingListResponse | undefined,
): TimeTrackingListResponse | undefined {
  if (!own && !member) return undefined;
  // De-duplicate by id before sorting. In correct operation this is a no-op:
  // the two scopes read different SCHEMAS, so one timeTrackingId cannot appear
  // in both. It is here because the failure mode is disproportionate — a
  // server (or fixture) that ignores `scope` returns the same rows twice, and
  // React then renders duplicate keys, which corrupts row identity and
  // selection rather than merely showing a row twice.
  const seen = new Set<string>();
  const items = [...(own?.items ?? []), ...(member?.items ?? [])]
    .filter((t) => {
      if (seen.has(t.timeTrackingId)) return false;
      seen.add(t.timeTrackingId);
      return true;
    })
    .sort((a, b) =>
      a.startTime < b.startTime ? 1 : a.startTime > b.startTime ? -1 : 0,
    );
  return {
    items,
    total: (own?.total ?? 0) + (member?.total ?? 0),
    page: own?.page ?? 1,
    pageSize: own?.pageSize ?? items.length,
    totals: addTotals(own?.totals, member?.totals),
  };
}

/** Sum two totals blocks; either side may be absent while its query settles. */
function addTotals(
  a: TimeTrackingTotals | undefined,
  b: TimeTrackingTotals | undefined,
): TimeTrackingTotals {
  const zero: TimeTrackingTotals = {
    totalMinutes: 0,
    weatherMinutes: 0,
    technicalMinutes: 0,
    otherMinutes: 0,
    lockedMinutes: 0,
  };
  const x = a ?? zero;
  const y = b ?? zero;
  return {
    totalMinutes: x.totalMinutes + y.totalMinutes,
    weatherMinutes: x.weatherMinutes + y.weatherMinutes,
    technicalMinutes: x.technicalMinutes + y.technicalMinutes,
    otherMinutes: x.otherMinutes + y.otherMinutes,
    lockedMinutes: x.lockedMinutes + y.lockedMinutes,
  };
}

export function useCreateTimeTrackingMutation() {
  const qc = useQueryClient();
  return useMutation<TimeTracking, Error, TimeTrackingCreateRequest>({
    mutationFn: (body) =>
      timeTrackingFetch<TimeTracking>("/trackings", { method: "POST", body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: timeTrackingKeys.all });
    },
  });
}

export function useUpdateTimeTrackingMutation() {
  const qc = useQueryClient();
  return useMutation<
    TimeTracking,
    Error,
    { id: string; body: TimeTrackingPatchRequest }
  >({
    mutationFn: ({ id, body }) =>
      timeTrackingFetch<TimeTracking>(`/trackings/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body,
      }),
    // Invalidate on error too: a 409 `locked` means the row changed underneath
    // us, so the stale copy on screen has to be replaced either way.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: timeTrackingKeys.all });
    },
  });
}

export function useDeleteTimeTrackingMutation() {
  const qc = useQueryClient();
  return useMutation<undefined, Error, string>({
    mutationFn: (id) =>
      timeTrackingFetch<undefined>(`/trackings/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: timeTrackingKeys.all });
    },
  });
}

/** Bulk "mark as invoiced". Idempotent server-side; see contract TT-D5. */
export function useLockTimeTrackingsMutation() {
  const qc = useQueryClient();
  return useMutation<TimeTrackingLockResponse, Error, string[]>({
    mutationFn: (ids) =>
      timeTrackingFetch<TimeTrackingLockResponse>("/trackings/lock", {
        method: "POST",
        body: { ids },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: timeTrackingKeys.all });
    },
  });
}

export function useTimeTrackingConfigQuery(
  projectId: string | null,
  options: { enabled?: boolean } = {},
) {
  const tenantReady = useTenantReady();
  return useQuery<TimeTrackingConfig>({
    queryKey: timeTrackingKeys.config(projectId ?? "none"),
    queryFn: () => {
      if (projectId == null) throw new Error("projectId is required");
      return timeTrackingFetch<TimeTrackingConfig>(
        `/projects/${encodeURIComponent(projectId)}/config`,
      );
    },
    enabled: tenantReady && projectId != null && (options.enabled ?? true),
    staleTime: 60_000,
    retry: false,
  });
}

export function usePutTimeTrackingConfigMutation(projectId: string) {
  const qc = useQueryClient();
  return useMutation<TimeTrackingConfig, Error, TimeTrackingConfigPutRequest>({
    mutationFn: (body) =>
      timeTrackingFetch<TimeTrackingConfig>(
        `/projects/${encodeURIComponent(projectId)}/config`,
        { method: "PUT", body },
      ),
    onSuccess: (updated) => {
      qc.setQueryData(timeTrackingKeys.config(projectId), updated);
      // The review totals bar multiplies hours by these prices — it has to
      // re-render against the new config, not the one it was mounted with.
      void qc.invalidateQueries({ queryKey: timeTrackingKeys.lists() });
    },
  });
}
